// FR-2 空弦与音阶练习。三模式:
//   - 长音 (long):  持续一个音 ≥ target ms,综合分 = 0.6×音准(RMSE) + 0.3×时值 + 0.1×起音清晰度
//   - 音阶 (scale): 按顺序拉 8 个音(空弦→1把→…→7把),综合分 = 0.4×音准 + 0.3×节奏 + 0.3×运弓 (FR-2.1)
//   - 单音 (click): 每拉一下评一次,|cents|≤30=100 / 30~50=60 / >50 或超时=0
// 视觉/收音组件与调音页(tuning.ts)、音色页(timbre.ts)保持一致。

import type { Page } from '../page';
import { el, scoreRing } from '../components';
import { state, toast } from '../store';
import { showMicError } from '../mic';
import { VIOLIN_STRINGS } from '../../data/pitches';
import { midiToName } from '../../audio/freq';
import {
  pitchScoreFromRMSE,
  bowingSteadiness,
  combinedPracticeScore,
  singleNoteScore,
  timeScoreFromDuration,
} from '../../assessment/score';
import type { StringType } from '../../types';

type Mode = 'long' | 'scale' | 'click';

interface ModeDef {
  id: Mode;
  label: string;
  hint: string;
  targetMs: number;
}

const MODES: ModeDef[] = [
  { id: 'long', label: '长音', hint: '持续一个音 ≥ 2 秒,评音准+时值+起音', targetMs: 2000 },
  { id: 'scale', label: '音阶', hint: '按顺序拉 8 个音(空弦→7 把),评音准+节奏+运弓', targetMs: 1500 },
  { id: 'click', label: '单音', hint: '每拉一下评一次,|cents|≤30=100 / 30~50=60 / 超时=0', targetMs: 0 },
];

const STRING_KEYS = ['G3', 'D4', 'A4', 'E5'] as const;
const STRING_LABELS = ['G 弦', 'D 弦', 'A 弦', 'E 弦'];
const POSITIONS = [0, 1, 2, 3, 4, 5, 6, 7]; // 0 = 空弦
const POS_LABELS = ['空弦', '1 把', '2 把', '3 把', '4 把', '5 把', '6 把', '7 把'];
const CLICK_TIMEOUT_MS = 5000;

export function practicePage(): Page {
  let cleanup: (() => void) | null = null;
  return {
    mount(container) {
      // ============ 状态 ============
      let mode: Mode = 'long';
      let stringIdx: StringType = 2; // A
      let position = 0;
      let volumeThresholdPct = 12;
      let targetMs = MODES[0].targetMs;

      let unsub: (() => void) | null = null;
      let refOsc: { osc: OscillatorNode; gain: GainNode } | null = null;
      let refGainPct = 25; // 0..100
      let rafId = 0;

      // 录音期累积状态
      const centsBuf: { t: number; cents: number; rms: number }[] = [];
      const allCents: number[] = [];
      const allRms: number[] = [];
      let attackRms = 0;
      let sustainedRmsSum = 0;
      let sustainedFrames = 0;
      let attackCaptured = false;
      let aboveMs = 0;
      let lastFrameT = 0;

      // 音阶模式
      let scaleNoteIdx = 0;
      let scaleNoteStart = 0;
      let scaleNoteCents: number[] = [];
      const scaleNoteDurations: number[] = [];
      const scaleNoteScores: { pos: number; cents: number; score: number }[] = [];

      // 单音模式
      let clickActive = false;
      let clickStartT = 0;
      let clickCents: number[] = [];
      const clickResults: { cents: number; score: number; timedOut: boolean }[] = [];

      // ============ UI 元素 ============
      const statusEl = el('div', { class: 'tiny muted', text: '选择模式与目标,开始练习' });
      const progressEl = el('div', { class: 'tiny muted', style: 'display:none' });

      const liveBig = el('div', { class: 'big-num', text: '—' });
      const liveHz = el('div', { class: 'tiny muted', text: '等待琴声…' });
      const liveReadout = el('div', { class: 'freq-readout' }, [
        el('div', {}, [liveBig, el('span', { class: 'unit', text: ' Hz' })]),
        liveHz,
      ]);

      const centsBadge = el('div', { class: 'cents-badge', text: '—' });
      const targetNum = el('div', { class: 'cents-num', text: '—' });
      const targetCell = el('div', { class: 'cents-cell' }, [
        el('span', { class: 'tiny muted', text: '目标' }), targetNum,
      ]);
      const liveCell = el('div', { class: 'cents-cell' }, [
        el('span', { class: 'tiny muted', text: '偏差' }), centsBadge,
      ]);
      const centsRow = el('div', { class: 'cents-row' }, [targetCell, liveCell]);

      const curve = el('canvas', { class: 'practice-curve' }) as HTMLCanvasElement;
      const cctx = curve.getContext('2d')!;
      const dpr = window.devicePixelRatio || 1;

      // 音量条
      const volFill = el('div', { class: 'fill' });
      const volThresh = el('div', { class: 'thresh' });
      const volbar = el('div', { class: 'volbar' }, [volFill, volThresh]);
      const volLabel = el('span', { class: 'tiny muted', text: '音量 0% · 阈值 0%' });

      // 阈值滑块
      const thrSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(volumeThresholdPct) });
      const thrVal = el('span', { class: 'tiny muted', text: `阈值 ${volumeThresholdPct}%` });

      // 模式选择
      const modeChips: HTMLButtonElement[] = MODES.map((m) => {
        const c = el('button', { class: 'chip' + (mode === m.id ? ' active' : ''), text: m.label }) as HTMLButtonElement;
        c.dataset.mode = m.id;
        c.onclick = () => { mode = m.id; targetMs = m.targetMs; renderMode(); renderTargetControl(); resetRecording(); };
        return c;
      });
      const modeRow = el('div', { class: 'row wrap' }, modeChips);
      const modeHint = el('div', { class: 'tiny muted', text: MODES[0].hint });

      // 目标时长(仅长音可调)
      const targetSlider = el('input', { type: 'range', min: '1000', max: '6000', step: '500', value: String(targetMs) });
      const targetVal = el('span', { class: 'tiny muted', text: `${(targetMs / 1000).toFixed(1)} s` });

      // 弦选择
      const stringChips: HTMLButtonElement[] = STRING_LABELS.map((label, i) => {
        const b = el('button', { class: 'chip' + (i === stringIdx ? ' active' : ''), text: label }) as HTMLButtonElement;
        b.onclick = () => { stringIdx = i as StringType; updateTarget(); resetRecording(); };
        return b;
      });

      // 把位选择
      const posChips: HTMLButtonElement[] = POSITIONS.map((p) => {
        const b = el('button', { class: 'chip' + (position === p ? ' active' : ''), text: POS_LABELS[p] }) as HTMLButtonElement;
        b.onclick = () => { position = p; updateTarget(); resetRecording(); };
        return b;
      });
      const posRow = el('div', { class: 'row wrap' }, posChips);

      // 把位引导条(FR-2.1)
      const posGuideCells: HTMLElement[] = [];
      const posGuide = el('div', { class: 'pos-guide' });
      POSITIONS.forEach((p) => {
        const cell = el('div', { class: 'pos-guide-cell', text: POS_LABELS[p] });
        posGuide.append(cell);
        posGuideCells.push(cell);
      });

      // 控制按钮
      const startBtn = el('button', { class: 'primary', text: '开始练习' }) as HTMLButtonElement;
      const stopBtn = el('button', { class: 'ghost', text: '停止', style: 'display:none' }) as HTMLButtonElement;
      const refToggle = el('button', { class: 'chip', text: '🎵 参考音' }) as HTMLButtonElement;
      const refVolSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(refGainPct), class: 'ref-vol' }) as HTMLInputElement;
      const refVolLabel = el('span', { class: 'tiny muted', text: `音量 ${refGainPct}%` });
      refVolSlider.oninput = () => {
        refGainPct = Number(refVolSlider.value);
        refVolLabel.textContent = `音量 ${refGainPct}%`;
        if (refOsc) refOsc.gain.gain.value = refGainPct / 100 * 0.3;
      };
      const resultCard = el('div');

      // ============ 辅助函数 ============
      function targetHz(): number {
        const key = STRING_KEYS[stringIdx];
        const scale = state.prefs.A4 / 440;
        if (position === 0) return VIOLIN_STRINGS[key].open * scale;
        return ((VIOLIN_STRINGS[key].positions as Record<number, number>)[position] ?? VIOLIN_STRINGS[key].open) * scale;
      }
      function targetNoteName(): string {
        const midi = Math.round(69 + 12 * Math.log2(targetHz() / state.prefs.A4));
        return midiToName(midi);
      }
      function updateTarget() {
        targetNum.textContent = targetNoteName();
      }
      function renderMode() {
        modeChips.forEach((c, i) => c.classList.toggle('active', mode === MODES[i].id));
        modeHint.textContent = MODES.find((m) => m.id === mode)!.hint;
      }
      function renderTargetControl() {
        const editable = mode === 'long';
        targetSlider.disabled = !editable;
        targetSlider.style.opacity = editable ? '1' : '.4';
        targetVal.textContent = editable ? `${(targetMs / 1000).toFixed(1)} s` : '固定';
      }
      function updateVolMarker() { volThresh.style.left = `${volumeThresholdPct}%`; }

      function resetRecording() {
        centsBuf.length = 0;
        allCents.length = 0;
        allRms.length = 0;
        attackRms = 0; sustainedRmsSum = 0; sustainedFrames = 0; attackCaptured = false;
        aboveMs = 0; lastFrameT = 0;
        scaleNoteIdx = 0; scaleNoteStart = 0; scaleNoteCents = [];
        scaleNoteDurations.length = 0;
        scaleNoteScores.length = 0;
        clickActive = false; clickStartT = 0; clickCents = [];
        clickResults.length = 0;
        progressEl.style.display = 'none';
        progressEl.textContent = '';
        resultCard.innerHTML = '';
        centsBadge.textContent = '—';
        centsBadge.className = 'cents-badge';
        liveBig.textContent = '—';
        liveHz.textContent = '等待琴声…';
        drawCurve();
        posGuideCells.forEach((c) => { c.classList.remove('target', 'detected', 'mismatch'); });
        if (mode !== 'scale') posGuideCells[position]?.classList.add('target');
        if (mode === 'long') progressEl.style.display = '';
      }

      async function startRef() {
        const ctx = await state.engine.ensureAudio();
        if (!ctx) return;
        if (refOsc) { refOsc.osc.stop(); refOsc = null; }
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine'; osc.frequency.value = targetHz(); g.gain.value = refGainPct / 100 * 0.3;
        osc.connect(g).connect(ctx.destination); osc.start();
        refOsc = { osc, gain: g };
      }
      refToggle.onclick = () => {
        if (refOsc) { refOsc.osc.stop(); refOsc = null; refToggle.classList.remove('active'); }
        else { startRef(); refToggle.classList.add('active'); }
      };
      thrSlider.oninput = () => { volumeThresholdPct = Number(thrSlider.value); thrVal.textContent = `阈值 ${volumeThresholdPct}%`; updateVolMarker(); };
      targetSlider.oninput = () => { targetMs = Number(targetSlider.value); targetVal.textContent = `${(targetMs / 1000).toFixed(1)} s`; };

      // ============ 曲线绘制 ============
      function drawCurve() {
        const w = curve.clientWidth || 600;
        const h = curve.clientHeight || 140;
        curve.width = w * dpr; curve.height = h * dpr;
        const ctx = cctx;
        ctx.clearRect(0, 0, curve.width, curve.height);
        ctx.strokeStyle = 'rgba(124,92,255,.18)'; ctx.lineWidth = 1;
        for (let i = 1; i < 4; i++) {
          const y = (h / 4) * i * dpr;
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(curve.width, y); ctx.stroke();
        }
        ctx.strokeStyle = 'rgba(124,92,255,.3)';
        ctx.beginPath(); ctx.moveTo(0, h / 2 * dpr); ctx.lineTo(curve.width, h / 2 * dpr); ctx.stroke();
        const y5Top = h / 2 * dpr - (5 / 50) * (h / 2 * dpr);
        const y5Bot = h / 2 * dpr + (5 / 50) * (h / 2 * dpr);
        ctx.fillStyle = 'rgba(61,220,132,.12)';
        ctx.fillRect(0, y5Top, curve.width, y5Bot - y5Top);
        if (centsBuf.length < 2) return;
        const tMin = centsBuf[0].t;
        const tMax = Math.max(centsBuf[centsBuf.length - 1].t, tMin + 1);
        const span = Math.max(tMax - tMin, 100);
        ctx.strokeStyle = '#4cc9f0';
        ctx.lineWidth = 2 * dpr;
        ctx.beginPath();
        for (let i = 0; i < centsBuf.length; i++) {
          const x = ((centsBuf[i].t - tMin) / span) * curve.width;
          const c = Math.max(-50, Math.min(50, centsBuf[i].cents));
          const y = h / 2 * dpr - (c / 50) * (h / 2 * dpr);
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      function curveLoop() { drawCurve(); rafId = requestAnimationFrame(curveLoop); }

      // ============ 把位检测 ============
      function detectPosition(freq: number): number {
        const key = STRING_KEYS[stringIdx];
        const scale = state.prefs.A4 / 440;
        const candidates: number[] = [VIOLIN_STRINGS[key].open * scale];
        for (let p = 1; p <= 7; p++) {
          candidates.push(((VIOLIN_STRINGS[key].positions as Record<number, number>)[p] ?? candidates[candidates.length - 1]) * scale);
        }
        let best = 0; let bestCents = Infinity;
        for (let i = 0; i < candidates.length; i++) {
          const c = Math.abs(1200 * Math.log2(freq / candidates[i]));
          if (c < bestCents) { bestCents = c; best = i; }
        }
        return best;
      }

      function updatePositionGuide(freq: number) {
        if (mode === 'scale') return;
        const detected = detectPosition(freq);
        posGuideCells.forEach((c, i) => {
          c.classList.toggle('detected', i === detected);
          if (detected !== position) c.classList.add('mismatch');
          else c.classList.remove('mismatch');
        });
      }

      // ============ 进度条 ============
      function updateProgress() {
        if (mode === 'long') {
          progressEl.textContent = `已发音 ${(aboveMs / 1000).toFixed(1)} s / 目标 ${(targetMs / 1000).toFixed(1)} s`;
        } else if (mode === 'scale') {
          const cur = Math.min(scaleNoteIdx + 1, POSITIONS.length);
          progressEl.textContent = `音阶 ${cur}/${POSITIONS.length} · ${POS_LABELS[scaleNoteIdx] ?? '完成'}`;
        } else if (mode === 'click') {
          if (clickActive) {
            const left = Math.max(0, (CLICK_TIMEOUT_MS - (performance.now() - clickStartT)) / 1000).toFixed(1);
            progressEl.textContent = `单音倒计时 ${left}s · 已评 ${clickResults.length} 次`;
          } else {
            progressEl.textContent = `已评 ${clickResults.length} 次 · 拉一下评一个音`;
          }
        }
      }

      // ============ 录音主流程 ============
      async function start() {
        startBtn.disabled = true;
        try {
          await state.engine.start();
        } catch (e) {
          startBtn.disabled = false;
          showMicError(e);
          return;
        }
        resetRecording();
        startBtn.style.display = 'none'; stopBtn.style.display = '';
        progressEl.style.display = '';
        statusEl.textContent = '聆听中…';
        curveLoop();

        unsub = state.engine.onFrameEvent((f) => {
          const thrRms = (volumeThresholdPct / 100) * 0.2;
          const volPct = Math.min(100, (f.rms / 0.2) * 100);
          volFill.style.width = `${volPct}%`;
          volLabel.textContent = `音量 ${volPct.toFixed(0)}% · 阈值 ${volumeThresholdPct}%`;

          const above = f.rms >= thrRms;
          const now = f.time;
          if (lastFrameT) aboveMs += (now - lastFrameT) * (above ? 1 : 0);
          lastFrameT = now;
          if (above && !attackCaptured) { attackRms = f.rms; attackCaptured = true; }
          if (above) { sustainedRmsSum += f.rms; sustainedFrames += 1; }

          if (!above || f.confidence < 0.4 || f.freq <= 0) {
            liveHz.textContent = above ? '音量低' : '等待琴声…';
            if (mode === 'click' && clickActive && now - clickStartT > CLICK_TIMEOUT_MS) {
              clickResults.push({ cents: NaN, score: 0, timedOut: true });
              clickActive = false;
              clickCents = [];
              toast('⏱ 超时,该音计 0 分');
            }
            updateProgress();
            return;
          }

          const cents = 1200 * Math.log2(f.freq / targetHz());
          centsBuf.push({ t: now, cents, rms: f.rms });
          if (centsBuf.length > 600) centsBuf.shift();
          allCents.push(cents);
          allRms.push(f.rms);

          liveBig.textContent = f.freq.toFixed(1);
          liveHz.textContent = `${f.freq.toFixed(1)} Hz · conf ${(f.confidence * 100).toFixed(0)}%`;
          centsBadge.textContent = `${cents > 0 ? '+' : ''}${cents.toFixed(0)} ¢`;
          const abs = Math.abs(cents);
          centsBadge.className = 'cents-badge ' + (abs < 5 ? 'good' : abs < 15 ? 'warn' : 'bad');

          updatePositionGuide(f.freq);

          if (mode === 'long') {
            if (aboveMs >= targetMs) stop();
          } else if (mode === 'scale') {
            scaleNoteCents.push(cents);
            if (!scaleNoteStart) scaleNoteStart = now;
            if (now - scaleNoteStart >= targetMs) {
              const meanCents = meanOr(scaleNoteCents, 0);
              scaleNoteScores.push({ pos: scaleNoteIdx, cents: meanCents, score: singleNoteScore(meanCents) });
              scaleNoteDurations.push(now - scaleNoteStart);
              scaleNoteIdx += 1;
              scaleNoteCents = [];
              scaleNoteStart = now;
              if (scaleNoteIdx >= POSITIONS.length) stop();
            }
          } else if (mode === 'click') {
            if (!clickActive) {
              clickActive = true;
              clickStartT = now;
              clickCents = [];
              posGuideCells.forEach((c) => c.classList.remove('mismatch'));
            }
            clickCents.push(cents);
            // 用户停手(rms 下降)→ 提交当前音;或达到时间窗也提交
            if (now - clickStartT > 800 && meanOr(clickCents, 0) !== 0) {
              const meanC = meanOr(clickCents, 0);
              const tooLate = now - clickStartT > CLICK_TIMEOUT_MS;
              clickResults.push({ cents: meanC, score: tooLate ? 0 : singleNoteScore(meanC), timedOut: tooLate });
              clickActive = false;
              clickCents = [];
            }
          }
          updateProgress();
        });
      }

      function stop() {
        unsub?.(); unsub = null;
        cancelAnimationFrame(rafId);
        if (refOsc) { refOsc.osc.stop(); refOsc = null; refToggle.classList.remove('active'); }
        state.engine.stop();
        startBtn.style.display = ''; startBtn.disabled = false; stopBtn.style.display = 'none';
        progressEl.style.display = 'none';
        statusEl.textContent = '已停止';
        volFill.style.width = '0%';
        computeResult();
      }

      // ============ 评分 ============
      function computeResult() {
        resultCard.innerHTML = '';
        if (mode === 'long') return renderLong();
        if (mode === 'scale') return renderScale();
        return renderClick();
      }

      function renderLong() {
        if (aboveMs < 500 || allCents.length < 5) {
          resultCard.append(el('div', { class: 'empty', text: `发音不足 ${(aboveMs / 1000).toFixed(1)}s,再多拉一会儿` }));
          return;
        }
        const rmse = Math.sqrt(allCents.reduce((a, b) => a + b * b, 0) / allCents.length);
        const pitch = pitchScoreFromRMSE(rmse);
        const time = timeScoreFromDuration(aboveMs, targetMs);
        const sustainedRms = sustainedFrames > 0 ? sustainedRmsSum / sustainedFrames : 0;
        const ratio = sustainedRms > 0 ? attackRms / sustainedRms : 0;
        const clarity01 = Math.max(0, Math.min(1, (ratio - 1) / 4));
        const overall = combinedPracticeScore(pitch, time, clarity01);
        renderResultCard({
          title: '长音评分',
          overall,
          rows: [
            `音准 RMSE: ${rmse.toFixed(1)} cents · 得分 ${Math.round(pitch)}`,
            `时值: ${(aboveMs / 1000).toFixed(1)}s / 目标 ${(targetMs / 1000).toFixed(1)}s · 得分 ${Math.round(time * 100)}`,
            `起音清晰度: ${ratio.toFixed(2)} (理想 2~5) · 得分 ${Math.round(clarity01 * 100)}`,
            '总分 = 0.6×音准 + 0.3×时值 + 0.1×起音清晰度',
          ],
        });
        if (overall >= 80) toast('👍 长音很稳！');
      }

      function renderScale() {
        if (scaleNoteScores.length < 2) {
          resultCard.append(el('div', { class: 'empty', text: '音阶采样不足,请至少完成 2 个音' }));
          return;
        }
        const pitch = meanOr(scaleNoteScores.map((n) => n.score), 0);
        const durs = scaleNoteDurations;
        const meanDur = meanOr(durs, 0);
        const durVar = durs.length > 1 ? durs.reduce((a, b) => a + (b - meanDur) ** 2, 0) / durs.length : 0;
        const rhythm01 = meanDur > 0 ? Math.max(0, Math.min(1, 1 - durVar / (meanDur * meanDur * 0.5))) : 0;
        const bowing = bowingSteadiness(allCents, allRms);
        const overall = 0.4 * pitch + 0.3 * rhythm01 * 100 + 0.3 * bowing * 100;
        const noteChips = scaleNoteScores.map((n) => {
          const cls = n.score >= 100 ? 'tag-ok' : n.score >= 60 ? 'tag-warn' : 'tag-bad';
          return el('span', { class: 'badge ' + cls, text: `${POS_LABELS[n.pos]} ${n.cents > 0 ? '+' : ''}${n.cents.toFixed(0)}¢` });
        });
        renderResultCard({
          title: '音阶评分',
          overall,
          rows: [
            `音准(单音平均): ${Math.round(pitch)}`,
            `节奏稳定性: ${(rhythm01 * 100).toFixed(0)}%`,
            `运弓平稳度: ${(bowing * 100).toFixed(0)}%`,
            '总分 = 0.4×音准 + 0.3×节奏 + 0.3×运弓 (FR-2.1)',
          ],
          extras: noteChips,
        });
        if (overall >= 80) toast('🎵 音阶很顺！');
      }

      function renderClick() {
        if (clickActive) {
          const cents = meanOr(clickCents, NaN);
          const timedOut = performance.now() - clickStartT > CLICK_TIMEOUT_MS;
          clickResults.push({ cents, score: timedOut ? 0 : singleNoteScore(cents), timedOut });
          clickActive = false;
        }
        if (clickResults.length === 0) {
          resultCard.append(el('div', { class: 'empty', text: '还没拉到音,请至少拉一下' }));
          return;
        }
        const valid = clickResults.filter((r) => !r.timedOut);
        const avgScore = meanOr(clickResults.map((r) => r.score), 0);
        const last = clickResults[clickResults.length - 1];
        const noteChips = clickResults.map((r, i) => {
          const cls = r.timedOut ? 'tag-bad' : r.score >= 100 ? 'tag-ok' : r.score >= 60 ? 'tag-warn' : 'tag-bad';
          const label = r.timedOut ? `第 ${i + 1} 次 超时` : `第 ${i + 1} 次 ${r.cents > 0 ? '+' : ''}${r.cents.toFixed(0)}¢`;
          return el('span', { class: 'badge ' + cls, text: label });
        });
        renderResultCard({
          title: '单音评分',
          overall: avgScore,
          rows: [
            `最近一次: ${last.timedOut ? '⏱ 超时(0)' : `${last.cents > 0 ? '+' : ''}${last.cents.toFixed(0)}¢ → ${last.score} 分`}`,
            `平均: ${avgScore.toFixed(0)} 分(共 ${clickResults.length} 次,有效 ${valid.length})`,
            '规则: |cents|≤30 → 100, 30~50 → 60, >50 或超时 → 0',
          ],
          extras: noteChips,
        });
        if (avgScore >= 90 && clickResults.length >= 3) toast('🎯 单音很准！');
      }

      function renderResultCard(r: { title: string; overall: number; rows: string[]; extras?: HTMLElement[] }) {
        resultCard.append(el('div', { class: 'card' }, [
          el('h2', { text: r.title }),
          el('div', { class: 'row center', style: 'gap:18px' }, [
            scoreRing(Math.round(r.overall)),
            el('div', { class: 'col tiny' }, r.rows.map((t) => el('span', { text: t }))),
          ]),
          ...(r.extras && r.extras.length ? [el('h2', { text: '逐音明细' }), el('div', { class: 'row wrap', style: 'gap:6px' }, r.extras)] : []),
        ]));
      }

      startBtn.onclick = start;
      stopBtn.onclick = stop;

      // ============ 初始渲染 + 装配 ============
      updateVolMarker();
      updateTarget();
      renderTargetControl();
      resetRecording();

      const card = el('div', { class: 'card' }, [
        el('div', { class: 'row center', style: 'margin-bottom:8px' }, [startBtn, stopBtn]),
        liveReadout,
        centsRow,
        curve,
        el('div', { class: 'row between' }, [volLabel, el('span', { class: 'tiny muted', text: '▾ = 阈值' })]),
        volbar,
        progressEl,
      ]);
      const targetCard = el('div', { class: 'card' }, [
        el('h2', { text: '练习模式' }),
        modeRow,
        modeHint,
        el('h2', { text: '目标弦' }),
        el('div', { class: 'row wrap' }, stringChips),
        el('h2', { text: '把位(单音/长音)' }),
        posRow,
        el('h2', { text: '把位引导(FR-2.1)' }),
        posGuide,
        el('h2', { text: '目标时长(长音可调)' }),
        el('label', { class: 'field' }, [targetVal, targetSlider]),
        el('div', { class: 'row center', style: 'margin-top:8px;gap:10px;flex-wrap:wrap' }, [
          refToggle,
          el('div', { class: 'ref-vol-wrap' }, [refVolSlider, refVolLabel]),
        ]),
      ]);
      const paramCard = el('div', { class: 'card' }, [
        el('h2', { text: '采集参数' }),
        el('label', { class: 'field' }, [thrVal, thrSlider]),
        el('p', { class: 'tiny muted', text: '阈值:音量低于此值视为静音,曲线与计时均冻结。' }),
      ]);

      container.append(
        el('div', { class: 'row between', style: 'align-items:baseline' }, [
          el('div', {}, [el('h1', { text: '空弦与音阶练习' }), el('p', { class: 'sub', text: '长音 / 音阶 / 单音 · 实时音准曲线 + 综合评分' })]),
          statusEl,
        ]),
        card,
        targetCard,
        paramCard,
        resultCard,
        el('p', { class: 'tiny muted', text: '🔒 音频仅在本地处理;评分实时计算,不上传。' }),
      );

      cleanup = stop;
    },
    unmount() {
      cleanup?.();
      cleanup = null;
    },
  };
}

function meanOr(xs: number[], fallback: number): number {
  if (!xs.length) return fallback;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}